import { DateTime } from 'luxon';
import {
  CoachBranding,
  CoachClientSlotLoginProjection,
  CoachClientStatus,
  CoachRelationship,
  coachRelAsClientId,
} from './coach';
import { RosterState } from './coach-license-mirror-apply';

/**
 * Rebuilding a client's missing coach link (#4690).
 *
 * ## What goes wrong without this
 *
 * A client's own database holds one document naming their coach:
 * `coach-relationship/as-client`. Its `coachUid` is what starts the replication
 * that copies the coach's shared exercises down to that client, and it also
 * drives coach branding and the coach section of Settings.
 *
 * That document is CREATED in exactly one place — the last stage of the claim,
 * running on the client's own phone, at the single moment they accept the
 * coach's invitation. If that write does not happen, or does not survive,
 * nothing ever creates it again. The client is then coached as far as the
 * server is concerned and not coached as far as their own app is concerned,
 * permanently, with nothing reporting it. One customer was found in exactly
 * that state, having received none of his coach's 25 shared exercises while his
 * coach's four other clients received theirs.
 *
 * The server holds the same relationship independently and correctly — the
 * grant on the client's account record, and the coach's own roster — so the
 * repair is simply to compare the two and write the client's copy back when it
 * is missing.
 *
 * ## Why this is a separate, pure module
 *
 * The decision has several refusals that matter more than the repair itself
 * (see below), and each one fails silently if it is wrong: a wrong refusal
 * leaves a client permanently unlinked, and a wrong repair hands somebody back
 * a coach they deliberately left. Keeping the rule free of CouchDB lets every
 * one of those branches be tested directly.
 */

/**
 * How long after a claim the repair starts being willing to act.
 *
 * This is a RACE GUARD, not a policy. During a real claim the client writes
 * `coach-relationship/as-client` to its own device first, then signs in — and
 * signs in up to four more times while it waits for its new role to propagate —
 * and only pushes the document to the server after all of that. A repair that
 * ran inside that window would create a second, independent first revision of a
 * document the client is about to push its own first revision of, which is a
 * genuine sync conflict on essentially every claim.
 *
 * An hour is far longer than that window needs, because being slow to repair
 * costs nothing: the client simply gets repaired at their next sign-in instead.
 */
export const COACH_LINK_REPAIR_MIN_CLAIM_AGE_MS = 60 * 60 * 1000;

/** What the repair decided to do about one signing-in client's coach link. */
export enum CoachLinkRepairOutcome {
  /** The document is missing and should be created. */
  Create = 'create',
  /** The document already names this coach. Nothing to do. */
  AlreadyLinked = 'already-linked',
  /** Not confirmed on the coach's roster, so the relationship is not proven live. */
  NotOnRoster = 'not-on-roster',
  /** The document exists with no coach — the shape a detach deliberately leaves. */
  DeliberatelyDetached = 'deliberately-detached',
  /** The document exists and names someone else. Reported, never overwritten. */
  NamesAnotherCoach = 'names-another-coach',
  /** The claim is too recent to rule out racing the client's own write. */
  ClaimTooRecent = 'claim-too-recent',
  /** No claimed slot for this client, so there is nothing to rebuild from. */
  NoClaimedSlot = 'no-claimed-slot',
}

/**
 * A discriminated union rather than one shape with an optional body, so that
 * "only a Create carries a document" is something the compiler holds rather
 * than something a comment claims. `LoginMirrorAction` in
 * `coach-license-mirror-apply.ts` models the neighbouring decision the same
 * way, and for the same reason: the call site then needs no assertion.
 */
export type CoachLinkRepairDecision =
  | { outcome: CoachLinkRepairOutcome.Create; doc: CoachRelationship }
  | { outcome: Exclude<CoachLinkRepairOutcome, CoachLinkRepairOutcome.Create> };

/**
 * The reads and the one write this repair needs.
 *
 * Kept apart from `CoachLicenseMirrorIo` on purpose. That interface is
 * implemented by the `tools/` CLI as well as the server, and the CLI never runs
 * this repair — folding these in would make it implement three accessors it has
 * no use for, or make them optional, which would let the repair silently not
 * happen if one were ever left out.
 */
export interface CoachLinkRepairIo {
  /** Read the client's own `coach-relationship/as-client`, or null when absent. */
  readCoachRelAsClient: (clientUid: string) => Promise<CoachRelationship | null | undefined>;
  /**
   * The coach's client slots, projected. Only ever called when a repair might
   * actually happen, so the cost of the wider query stays off the ordinary
   * sign-in path. MUST THROW rather than answer with an empty list when the
   * read fails: an empty list reads as "no claimed slot", which would silently
   * turn a transient outage into a refusal to repair.
   */
  readCoachClientSlots: (coachUid: string) => Promise<CoachClientSlotLoginProjection[]>;
  /**
   * Create the client's `as-client` document. MUST NOT overwrite an existing
   * one — a conflict means the client's own copy arrived first, which is the
   * good outcome and must be left alone.
   */
  createCoachRelAsClient: (clientUid: string, doc: CoachRelationship) => Promise<void>;
}

/**
 * Pick the slot a rebuilt link should be based on.
 *
 * A coach can hold more than one slot for the same person — the claim adds a
 * roster entry per claimed invite with no duplicate check — so this has to
 * choose. It takes the most recently claimed one, because that is the
 * relationship currently in force. Ties, including several slots that all
 * predate the claim stamp, fall back to the slot id so that two servers
 * deciding at the same moment always reach the same answer.
 */
function pickClaimedSlot(
  slots: CoachClientSlotLoginProjection[],
  clientUid: string,
): CoachClientSlotLoginProjection | undefined {
  const mine = slots.filter(s => s.status === CoachClientStatus.Claimed && s.clientUid === clientUid);
  if (mine.length === 0) return undefined;
  return mine.sort((a, b) => {
    const byClaim = (b.claimedAt_utc ?? '').localeCompare(a.claimedAt_utc ?? '');
    if (byClaim !== 0) return byClaim;
    return (a._id ?? '').localeCompare(b._id ?? '');
  })[0];
}

/** Whether a claim is old enough that the client's own write cannot still be in flight. */
function claimIsSettled(slot: CoachClientSlotLoginProjection, now: DateTime): boolean {
  // No stamp means the slot was claimed before the stamp existed, which is by
  // definition long ago. Reading it as "settled" is what lets this repair heal
  // the accounts that were already stranded when it shipped.
  if (!slot.claimedAt_utc) return true;
  const claimedAt = DateTime.fromISO(slot.claimedAt_utc);
  // An unreadable stamp is treated the same way as a missing one rather than
  // blocking the repair forever on a value nobody will ever go back and fix.
  if (!claimedAt.isValid) return true;
  return now.toMillis() - claimedAt.toMillis() >= COACH_LINK_REPAIR_MIN_CLAIM_AGE_MS;
}

/**
 * Decide whether a signing-in client's coach link needs rebuilding.
 *
 * The refusals carry more weight than the repair, so they are applied in this
 * order and each is here for its own reason:
 *
 * 1. **Not confirmed on the coach's roster → do nothing.** The roster is the
 *    authority on who is currently a client, and both detach paths remove the
 *    client from it. This is the guard that stops somebody who deliberately
 *    left their coach being handed that coach back. It deliberately refuses on
 *    `RosterUnknown` too — a roster we could not read is not evidence.
 * 2. **The document exists with no `coachUid` → do nothing.** That is precisely
 *    the shape both detach paths leave behind: they clear the coach and keep
 *    the document. So "present and empty" means deliberately detached, while
 *    "absent" means never written. This is belt-and-braces behind rule 1.
 * 3. **The document names a different coach → do nothing, and say so.**
 *    Overwriting a link that looks live is a worse mistake than leaving an
 *    inconsistency for a person to look at.
 * 4. **The claim is too recent → do nothing yet.** See
 *    {@link COACH_LINK_REPAIR_MIN_CLAIM_AGE_MS}.
 *
 * The rebuilt document is written in FULL rather than as a bare `coachUid`, and
 * that matters: if a minimal copy ever did collide with a client's own, the
 * conflict is closed without merging (the resolver cannot merge a document with
 * no `db__trans`, and the server never writes one), so whichever side wins
 * outright is the whole answer. `clientSlotId` in particular can only be
 * recovered here — the client cannot read its coach's database at all.
 */
export function decideCoachLinkRepair(input: {
  coachUid: string;
  clientUid: string;
  rosterState: RosterState;
  /** The client's current `as-client` document, or null/undefined when absent. */
  existing: CoachRelationship | null | undefined;
  /** The coach's client slots, projected. Empty when none were read. */
  slots: CoachClientSlotLoginProjection[];
  /** The coach's current branding, copied onto a rebuilt document. */
  branding: CoachBranding | undefined;
  now: DateTime;
}): CoachLinkRepairDecision {
  // rule 1: only a confirmed roster membership proves the relationship is live.
  if (input.rosterState !== RosterState.OnRoster) {
    return { outcome: CoachLinkRepairOutcome.NotOnRoster };
  }

  if (input.existing) {
    // rule 2: the deliberate-detach shape.
    if (!input.existing.coachUid) {
      return { outcome: CoachLinkRepairOutcome.DeliberatelyDetached };
    }
    // rule 3: someone else's link — report, never overwrite.
    if (input.existing.coachUid !== input.coachUid) {
      return { outcome: CoachLinkRepairOutcome.NamesAnotherCoach };
    }
    return { outcome: CoachLinkRepairOutcome.AlreadyLinked };
  }

  const slot = pickClaimedSlot(input.slots, input.clientUid);
  if (!slot) {
    return { outcome: CoachLinkRepairOutcome.NoClaimedSlot };
  }

  // rule 4: do not race the client's own write during a fresh claim.
  if (!claimIsSettled(slot, input.now)) {
    return { outcome: CoachLinkRepairOutcome.ClaimTooRecent };
  }

  const doc: CoachRelationship = { _id: coachRelAsClientId, coachUid: input.coachUid };
  // Only set what we actually know. Writing `undefined` into a CouchDB document
  // stores nothing, but leaving the keys off keeps the rebuilt document
  // identical in shape to one the client would have written itself.
  if (slot.coachName) doc.coachName = slot.coachName;
  if (input.branding) doc.branding = input.branding;
  if (slot._id) doc.clientSlotId = slot._id;
  return { outcome: CoachLinkRepairOutcome.Create, doc };
}
